import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import ArticleCard from '@/components/ArticleCard';
import HomeSidebar from '@/components/HomeSidebar';
import { Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PER_PAGE = 20;

export default function CategoryPage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get('page') || '1', 10);
  const sortBy = searchParams.get('sort') || 'date_desc'; // date_desc | date_asc | title_asc | title_desc
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [catName, setCatName] = useState('');
  const [catDesc, setCatDesc] = useState('');
  const [loading, setLoading] = useState(true);
  const [sidebarData, setSidebarData] = useState({ most_read: [], tags: [] });

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/public/articles?category=${slug}&page=${page}&limit=${PER_PAGE}&lang=${i18n.language}&sort=${sortBy}`)
      .then(r => r.json())
      .then(d => {
        let arr = d.articles || [];
        // Client-side fallback sort in case backend doesn't honour sort param.
        const lang = i18n.language || 'hr';
        const titleOf = (a) => (a.title_translations?.[lang] || a.title_hr || '').toLowerCase();
        const dateOf = (a) => new Date(a.published_at || a.created_at || 0).getTime();
        if (sortBy === 'date_asc') arr = [...arr].sort((a, b) => dateOf(a) - dateOf(b));
        else if (sortBy === 'date_desc') arr = [...arr].sort((a, b) => dateOf(b) - dateOf(a));
        else if (sortBy === 'title_asc') arr = [...arr].sort((a, b) => titleOf(a).localeCompare(titleOf(b), lang));
        else if (sortBy === 'title_desc') arr = [...arr].sort((a, b) => titleOf(b).localeCompare(titleOf(a), lang));
        setArticles(arr); setTotal(d.total || 0); setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [slug, page, i18n.language, sortBy]);

  useEffect(() => {
    fetch(`${API}/public/categories?lang=${i18n.language}`).then(r => r.json())
      .then(d => { const c = (d.categories || []).find(x => x.slug === slug); if (c) { setCatName(c.name_hr); setCatDesc(c.description_hr || ''); } });
  }, [slug, i18n.language]);

  useEffect(() => {
    fetch(`${API}/public/homepage?lang=${i18n.language}`).then(r => r.json())
      .then(d => setSidebarData({ most_read: d.most_read || [], tags: d.tags || [] }))
      .catch(() => {});
  }, [i18n.language]);

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const pageNums = Array.from({ length: totalPages }, (_, i) => i + 1).slice(Math.max(0, page - 3), page + 2);

  function goto(p) {
    if (p < 1 || p > totalPages) return;
    const next = new URLSearchParams(searchParams);
    next.set('page', String(p));
    setSearchParams(next);
  }

  function changeSort(value) {
    const next = new URLSearchParams(searchParams);
    next.set('sort', value);
    next.set('page', '1');
    setSearchParams(next);
  }

  return (
    <>
      <Helmet>
        <title>{`${catName || slug} — e-mina.eu`}</title>
        <meta name="description" content={catDesc || `${catName} - najnovije vijesti`} />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid={`category-page-${slug}`}>
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-wider text-gray-900 dark:text-gray-100 dark:text-white border-l-4 border-red-600 pl-3">{catName || slug}</h1>
          {catDesc && <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 pl-4">{catDesc}</p>}
          <div className="flex flex-wrap items-center justify-between mt-3 pl-4 gap-3">
            <p className="text-xs text-gray-400">{total} {t('pagination.total').toLowerCase()}</p>
            <label className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400" data-testid="category-sort-wrap">
              <span>Sortiraj:</span>
              <select value={sortBy} onChange={e => changeSort(e.target.value)}
                className="bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-gray-800 rounded px-2 py-1 text-xs"
                data-testid="category-sort-select">
                <option value="date_desc">Datum objave (najnovije prvo)</option>
                <option value="date_asc">Datum objave (najstarije prvo)</option>
                <option value="title_asc">Naslov A → Ž</option>
                <option value="title_desc">Naslov Ž → A</option>
              </select>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <main className="lg:col-span-9">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="animate-spin text-gray-400" /></div>
            ) : articles.length === 0 ? (
              <p className="text-center py-12 text-gray-400">{t('search.no_results')} "{catName}"</p>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="category-grid">
                  {articles.map(a => <ArticleCard key={a.id} article={a} />)}
                </div>

                {totalPages > 1 && (
                  <nav className="flex items-center justify-center gap-1 mt-10 text-sm" data-testid="category-pagination">
                    <button disabled={page <= 1} onClick={() => goto(page - 1)} className="px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded text-gray-700 dark:text-gray-300 disabled:opacity-30 hover:bg-gray-200 dark:hover:bg-gray-700">{t('pagination.prev')}</button>
                    {pageNums[0] > 1 && <span className="px-2 text-gray-400">...</span>}
                    {pageNums.map(p => (
                      <button key={p} onClick={() => goto(p)} className={`px-3 py-2 rounded font-medium ${p === page ? 'bg-red-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`} data-testid={`page-${p}`}>{p}</button>
                    ))}
                    {pageNums[pageNums.length - 1] < totalPages && <span className="px-2 text-gray-400">...</span>}
                    <button disabled={page >= totalPages} onClick={() => goto(page + 1)} className="px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded text-gray-700 dark:text-gray-300 disabled:opacity-30 hover:bg-gray-200 dark:hover:bg-gray-700">{t('pagination.next')}</button>
                  </nav>
                )}
              </>
            )}
          </main>

          <div className="lg:col-span-3">
            <HomeSidebar mostRead={sidebarData.most_read} tags={sidebarData.tags} />
          </div>
        </div>
      </div>
    </>
  );
}
